import React, { useState } from "react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { isEmptyStr, notJson } from "../util/stringHelper";
import { FormStack } from "../components/FormStack/FormStack";
import * as Yup from "yup";
import {
  VStack,
  Text,
  IconButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from "@chakra-ui/react";
import { CopyText } from "../components/Utilities/CopyText";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import { JsonTextAreaField } from "../components/fields/TextArea/JsonTextAreaField";
import {
  CheckIcon,
  ChevronUpIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
} from "@chakra-ui/icons";
import { useToast } from "../hooks/useToast";
import { cloneDeep } from "lodash";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { PlusIcon } from "../styles/icons";
import { useAdminApiClient } from "../hooks/useApiClient";

interface UserDetailFormValues {
  displayName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  customClaims: { key: string | null; value?: string; isNew: boolean }[];
}

interface UserDetailFormProps {
  apiClient: AdminApiClient;
  user: CherryHubAdminApi.UserAccount;
  isDisabled?: boolean;
  onCancel: (user?: CherryHubAdminApi.UserAccount) => void;
  onSuccess: (user: CherryHubAdminApi.UserAccount) => void;
  onFailure: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  isDisabled,
  submitForm,
  isValid,
  onSuccess,
  onFailure,
  onCancel,
  values,
  user,
  setFieldValue,
  errors,
}: UserDetailFormProps & FormikProps<UserDetailFormValues>) => {
  const apiClient = useAdminApiClient();
  const [userData, setUserData] = useState(user);

  const { data: claimOptions, isLoading: claimOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(
          AdminApiEnumConfigurations.SupportedCustomClaim
        ),
      []
    );

  return (
    <Box width="100%" alignSelf="flex-start">
      <FormStack>
        {user && (
          <HStack w="100%">
            <Box flexGrow={2} pb={4}>
              <CopyText text={user?.Uid} hover adjustMargin>
                <Text fontSize="md" color="cherryUi.600">
                  {user?.Uid}
                </Text>
              </CopyText>
              <Text fontSize="xl" color="cherryUi.600">
                <b>
                  {user?.DisplayName ??
                    user?.Email ??
                    user?.PhoneNumber ??
                    "Unknown"}
                </b>
              </Text>
            </Box>

            <Box>
              <Button
                colorScheme="cherryButton"
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting || !isValid}
                onClick={submitForm}
              >
                Save
              </Button>
            </Box>

            <Box>
              <Button
                disabled={isSubmitting}
                onClick={() =>
                  onCancel(user != userData ? userData : undefined)
                }
              >
                Cancel
              </Button>
            </Box>
          </HStack>
        )}

        <VStack
          w="100%"
          maxW="800px"
          alignItems="flex-start"
          pb={10}
          spacing={4}
        >
          <TextField horizontal label="Display Name" name="displayName" />

          <TextField horizontal label="Email" name="email" />

          <TextField horizontal label="Phone Number" name="phoneNumber" />
        </VStack>

        <Accordion w="100%" allowToggle variant="section" defaultIndex={[0]}>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <b>User Claim</b>
                    </Box>
                    {isExpanded ? (
                      <ChevronUpIcon color="white" fontSize="xl" />
                    ) : (
                      <AccordionIcon />
                    )}
                  </AccordionButton>
                </h2>
                <AccordionPanel width="100%">
                  <VStack
                    width="100%"
                    alignSelf="flex-start"
                    spacing={4}
                    mb={4}
                  >
                    <Button
                      alignSelf="flex-end"
                      mb={4}
                      size="sm"
                      leftIcon={<PlusIcon />}
                      onClick={() => {
                        values.customClaims.push({ key: null, isNew: true });
                        setFieldValue("customClaims", values.customClaims);
                      }}
                      colorScheme="cherryButton"
                    >
                      New Claim
                    </Button>

                    {(values.customClaims.length &&
                      values.customClaims.map((userClaim, claimIdx) => (
                        <UserClaimField
                          apiClient={apiClient}
                          uid={user.Uid}
                          key={claimIdx}
                          claimIdx={claimIdx}
                          claimOptions={claimOptions}
                          claimOptionsLoading={claimOptionsLoading}
                          customClaims={values.customClaims}
                          errors={
                            (errors.customClaims &&
                              (
                                errors.customClaims as FormikErrors<
                                  {
                                    key: string | null;
                                    value?: string;
                                    isNew: boolean;
                                  }[]
                                >
                              )[claimIdx]) ||
                            undefined
                          }
                          setFieldValue={setFieldValue}
                          userClaim={userClaim}
                          onFailure={onFailure}
                          setUserData={setUserData}
                          userData={userData}
                        />
                      ))) || <i>Custom Claims has not been set.</i>}
                  </VStack>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
        </Accordion>
      </FormStack>
    </Box>
  );
};

export const UserDetailForm = withFormik<
  UserDetailFormProps,
  UserDetailFormValues
>({
  mapPropsToValues: ({ user }) => {
    return {
      displayName: user?.DisplayName,
      email: user?.Email,
      phoneNumber: user?.PhoneNumber,
      customClaims: Object.entries(user.CustomClaims).map(([key, value]) => {
        return {
          key,
          value: notJson(JSON.stringify(value))
            ? (value as string)
            : JSON.stringify(value, null, 2),
          isNew: false,
        };
      }),
    };
  },

  handleSubmit: async (values, { props, setFieldError }) => {
    let isValid = true;
    if (isEmptyStr(values.displayName)) {
      setFieldError("displayName", "Display name is required.");
      isValid = false;
    }

    if (!isEmptyStr(values.email) && isEmptyStr(values.email)) {
      setFieldError("email", "Email is required.");
      isValid = false;
    }

    // Translate form state to UserUpdate
    const userAccount: Partial<CherryHubAdminApi.UserAccountUpdate> = {
      DisplayName: values.displayName,
      Email: values.email,
      PhoneNumber: values.phoneNumber,
    };

    let result = await props.apiClient.updateUser(props.user.Uid, userAccount);

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,

  validationSchema: Yup.object({
    displayName: Yup.string().required("Display Name is required!"),
    email: Yup.string()
      .email()
      .notRequired()
      .test(function (value) {
        const { phoneNumber } = this.parent;
        if (!phoneNumber)
          return value != null
            ? true
            : this.createError({
                message: "Either email or phone number is required!",
                path: "email",
              });
        return true;
      }),
    phoneNumber: Yup.string()
      .notRequired()
      .test(function (value) {
        const { email } = this.parent;
        if (!email)
          return value != null
            ? true
            : this.createError({
                message: "Either email or phone number is required!",
                path: "phoneNumber",
              });
        return true;
      }),
    customClaims: Yup.array().of(
      Yup.object({
        key: Yup.string().required("Claim Name is required!"),
        value: Yup.string()
          .required("Claim Value is required!")
          .test(function (value) {
            try {
              if (notJson(value)) return !isEmptyStr(value);
              JSON.parse(value);
            } catch {
              return this.createError({ message: "Not valid JSON" });
            }
            return true;
          }),
        isNew: Yup.boolean().required(),
      })
    ),
  }),
})(InnerForm);

interface UserClaimFieldProps {
  uid: string;
  apiClient: AdminApiClient;
  claimOptions: CherryHubAdminApi.EnumConfigurationItem[] | null;
  claimOptionsLoading: boolean;
  customClaims: { key: string | null; value?: string; isNew: boolean }[];
  userClaim: { key: string | null; value?: string; isNew: boolean };
  claimIdx: number;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  errors?: FormikErrors<{ key: string | null; value?: string; isNew: boolean }>;
  onFailure: (message?: string) => void;
  setUserData: React.Dispatch<
    React.SetStateAction<CherryHubAdminApi.UserAccount>
  >;
  userData: CherryHubAdminApi.UserAccount;
}

const UserClaimField = ({
  uid,
  apiClient,
  claimOptions,
  claimOptionsLoading,
  customClaims,
  userClaim,
  claimIdx,
  setFieldValue,
  errors,
  onFailure,
  setUserData,
  userData,
}: UserClaimFieldProps) => {
  const isNotExists =
    userClaim.key != null &&
    userClaim.value != null &&
    claimOptions?.find((option) => option.Key == userClaim.key) == null;

  const [editMode, setEditMode] = useState(false);

  const { showSuccessToast } = useToast();

  const saveCustomClaim = async (claimName: string, claimValue: string) => {
    const result = await apiClient.updateUserClaim(
      uid,
      claimName,
      notJson(claimValue) ? claimValue : JSON.parse(claimValue)
    );
    if (result.ok) {
      setEditMode(false);
      setFieldValue(
        "customClaims",
        Object.entries(result.data.CustomClaims).map(([key, value]) => {
          return {
            key,
            value: notJson(JSON.stringify(value))
              ? (value as string)
              : JSON.stringify(value, null, 2),
            isNew: false,
          };
        })
      );
      setUserData(result.data);
      showSuccessToast("Update claim success!");
    } else {
      if (onFailure) {
        onFailure(result.message);
      }
    }
  };

  const deleteCustomClaim = async (claimName: string) => {
    const result = await apiClient.deleteUserClaim(uid, claimName);
    if (result.ok) {
      customClaims?.splice(claimIdx, 1);
      setFieldValue("customClaims", customClaims);
      let userCopy = cloneDeep(userData);
      delete userCopy.CustomClaims[claimName];
      setUserData(userCopy);
      showSuccessToast("Delete claim success!");
    } else {
      if (onFailure) {
        onFailure(result.message);
      }
    }
  };

  return (
    <HStack w="100%" alignItems="flex-start">
      <Box flex={2}>
        <ChakraReactSelectField
          isDisabled={!userClaim.isNew}
          label={`Claim Name ${claimIdx + 1}`}
          name={`customClaims[${claimIdx}].key`}
          selectedOptionColor="blue"
          isLoading={claimOptionsLoading}
          options={
            claimOptions?.map((provider) => {
              return {
                label: provider.Value!,
                value: provider.Key!,
              };
            }) || []
          }
          className="relative-listbox"
        />
      </Box>
      <Box flex={3}>
        <JsonTextAreaField
          rows={1}
          isDisabled={!userClaim.isNew && (isNotExists || !editMode)}
          label={`Claim Value ${claimIdx + 1}`}
          name={`customClaims[${claimIdx}].value`}
          valueText={userClaim.value}
          errors={errors?.value}
          setFieldValue={setFieldValue}
        />
      </Box>

      <HStack h="100%" pt={9} alignSelf="center">
        {!editMode && (
          <>
            {userClaim.isNew ? (
              <IconButton
                aria-label={`Update claim.`}
                title={`Update claim.`}
                icon={<CheckIcon />}
                size="sm"
                onClick={() =>
                  saveCustomClaim(userClaim.key!, userClaim.value!)
                }
                isDisabled={errors != null}
              />
            ) : (
              <IconButton
                aria-label={`Edit claim.`}
                title={`Edit claim.`}
                icon={<EditIcon />}
                size="sm"
                onClick={() => {
                  setEditMode(true);
                }}
                isDisabled={isNotExists}
              />
            )}

            <IconButton
              aria-label={`Delete claim.`}
              title={`Delete claim.`}
              icon={<DeleteIcon />}
              size="sm"
              onClick={() => {
                if (userClaim.isNew) {
                  customClaims?.splice(claimIdx, 1);
                  setFieldValue("customClaims", customClaims);
                } else {
                  deleteCustomClaim(userClaim.key!);
                }
              }}
              isDisabled={isNotExists}
            />
          </>
        )}

        {editMode && (
          <>
            <IconButton
              aria-label={`Update claim.`}
              title={`Update claim.`}
              icon={<CheckIcon />}
              size="sm"
              onClick={() => saveCustomClaim(userClaim.key!, userClaim.value!)}
              isDisabled={errors != null}
            />

            <IconButton
              aria-label={`Cancel.`}
              title={`Cancel.`}
              icon={<CloseIcon />}
              size="sm"
              onClick={() => {
                setEditMode(false);
              }}
            />
          </>
        )}
      </HStack>
    </HStack>
  );
};
